// Derives per-form (not per-form-*type*) score boxes for the candidate
// drawer header, computed live from a candidate's raw form submissions
// rather than the possibly-stale finalSocioScore/finalAssesmentScore/
// finalInterviewScore fields on the cycle-status endpoint's response. See
// docs/superpowers/specs/2026-08-26-admin-per-form-scores-design.md.

import { FormDefinition, FormSubmissionRecord } from '../types/api';

export interface FormScoreEntry {
  formDefinitionId: string;
  /** Form's own display name; falls back to its formType if unnamed/unknown. */
  name: string;
  formType: string;
  /** Average of this form's submission ratings. */
  score: number;
  /** weightInFinalScore, used for sort order. */
  weight: number;
}

export function computeFormScores(
  formSubmissions: FormSubmissionRecord[],
  formDefMap: Map<string, FormDefinition>
): FormScoreEntry[] {
  const byForm = new Map<string, FormSubmissionRecord[]>();
  for (const sub of formSubmissions) {
    const arr = byForm.get(sub.formDefinitionId) ?? [];
    arr.push(sub);
    byForm.set(sub.formDefinitionId, arr);
  }

  const entries: FormScoreEntry[] = [];
  for (const [formDefinitionId, subs] of Array.from(byForm.entries())) {
    const ratings = subs
      .map(s => (s.submissionData.rating != null ? Number(s.submissionData.rating) : NaN))
      .filter(n => !Number.isNaN(n));
    if (ratings.length === 0) continue; // nothing to average — no box for this form

    const score = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
    const fd = formDefMap.get(formDefinitionId);
    // weightInFinalScore has been observed arriving as a numeric string on
    // the raw submission (e.g. "0.40") despite the TS type saying number —
    // always coerce, never trust the type. formDefMap's copy is already
    // Number-normalized upstream (SummaryPage), so it's used as-is.
    const fallbackWeight = Number(subs[0].weightInFinalScore);
    const weight = fd?.weightInFinalScore ?? (Number.isNaN(fallbackWeight) ? 0 : fallbackWeight);
    const name = fd?.name || subs[0].formType;
    const formType = subs[0].formType;

    entries.push({ formDefinitionId, name, formType, score, weight });
  }

  return entries.sort((a, b) => b.weight - a.weight || a.name.localeCompare(b.name));
}
